import { useTranslation } from 'react-i18next';
import type { QuotaCycle, QuotaProfileLimitInput } from '../../api.js';
import { CREDITS_PATTERN, toCredits, toMillis } from '../../components/AllocationPanel.js';
import type { LimitSubjectOption } from './CreditLimitForm.js';

const CYCLES: QuotaCycle[] = ['weekly', 'daily', 'monthly', 'lifetime'];

/** A limit row while it is being edited: the amount is the operator's raw text,
 *  so a half-typed "3" is not silently read as 3 credits on every keystroke. */
export interface LimitDraft {
  cycle: QuotaCycle;
  amount: string;
  catalogModelId: string;
  overageBehavior: 'deny' | 'warn';
}

export function dimensionKey(draft: { cycle: QuotaCycle; catalogModelId: string }): string {
  return `${draft.cycle}|${draft.catalogModelId || 'all'}`;
}

export function draftsFromLimits(
  limits: ReadonlyArray<{
    cycle: QuotaCycle;
    creditMillis: number;
    catalogModelId?: string;
    overageBehavior: 'deny' | 'warn';
  }>
): LimitDraft[] {
  return limits.map((limit) => ({
    cycle: limit.cycle,
    amount: String(toCredits(limit.creditMillis)),
    catalogModelId: limit.catalogModelId ?? '',
    overageBehavior: limit.overageBehavior
  }));
}

/** `undefined` when any row is not a valid credit amount — the caller keeps the
 *  save button disabled rather than sending a request that will 400. */
export function limitsFromDrafts(drafts: LimitDraft[]): QuotaProfileLimitInput[] | undefined {
  const out: QuotaProfileLimitInput[] = [];
  for (const draft of drafts) {
    const raw = draft.amount.trim();
    if (!CREDITS_PATTERN.test(raw)) return undefined;
    out.push({
      cycle: draft.cycle,
      creditMillis: toMillis(raw),
      ...(draft.catalogModelId ? { catalogModelId: draft.catalogModelId } : {}),
      overageBehavior: draft.overageBehavior
    });
  }
  return out;
}

export function QuotaProfileLimitsEditor({
  drafts,
  models,
  busy,
  onChange
}: {
  drafts: LimitDraft[];
  models: LimitSubjectOption[];
  busy: boolean;
  onChange: (next: LimitDraft[]) => void;
}) {
  const { t } = useTranslation();
  const taken = new Set(drafts.map(dimensionKey));

  const patch = (index: number, next: Partial<LimitDraft>) =>
    onChange(drafts.map((draft, at) => (at === index ? { ...draft, ...next } : draft)));

  /** The first (cycle, model) combination not already used, so "add" can never
   *  produce the duplicate the server would reject with a 409. */
  const freeDimension = (): LimitDraft | undefined => {
    for (const cycle of CYCLES) {
      for (const model of ['', ...models.map((option) => option.id)]) {
        if (!taken.has(dimensionKey({ cycle, catalogModelId: model }))) {
          return { cycle, amount: '', catalogModelId: model, overageBehavior: 'deny' };
        }
      }
    }
    return undefined;
  };
  const next = freeDimension();

  return (
    <div className="quota-profile-limits">
      <span className="quota-profile-limits-heading">{t('quotaProfiles.limits')}</span>
      {drafts.length === 0 ? (
        <p className="muted">{t('quotaProfiles.limitsEmpty')}</p>
      ) : null}
      {drafts.map((draft, index) => (
        <div className="quota-profile-limit-row" key={`${draft.cycle}-${draft.catalogModelId}`}>
          <label>
            <span>{t('creditLimits.cycle')}</span>
            <select
              value={draft.cycle}
              onChange={(event) => patch(index, { cycle: event.target.value as QuotaCycle })}
              disabled={busy}
            >
              {CYCLES.map((cycle) => (
                <option
                  key={cycle}
                  value={cycle}
                  // Already-used combinations are disabled at the source, so the
                  // operator never has to interpret a 409.
                  disabled={
                    cycle !== draft.cycle &&
                    taken.has(dimensionKey({ cycle, catalogModelId: draft.catalogModelId }))
                  }
                >
                  {t(`creditLimits.cycle_${cycle}`)}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>{t('creditLimits.model')}</span>
            <select
              value={draft.catalogModelId}
              onChange={(event) => patch(index, { catalogModelId: event.target.value })}
              disabled={busy}
            >
              <option
                value=""
                disabled={
                  draft.catalogModelId !== '' &&
                  taken.has(dimensionKey({ cycle: draft.cycle, catalogModelId: '' }))
                }
              >
                {t('creditLimits.modelAll')}
              </option>
              {models.map((model) => (
                <option
                  key={model.id}
                  value={model.id}
                  disabled={
                    model.id !== draft.catalogModelId &&
                    taken.has(dimensionKey({ cycle: draft.cycle, catalogModelId: model.id }))
                  }
                >
                  {model.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>{t('creditLimits.amount')}</span>
            <input
              value={draft.amount}
              inputMode="decimal"
              placeholder={t('creditLimits.amountPlaceholder')}
              onChange={(event) => patch(index, { amount: event.target.value })}
              disabled={busy}
              autoComplete="off"
            />
          </label>
          <div className="segmented-control">
            {(['deny', 'warn'] as const).map((behavior) => (
              <button
                key={behavior}
                type="button"
                className={draft.overageBehavior === behavior ? 'active' : ''}
                aria-pressed={draft.overageBehavior === behavior}
                onClick={() => patch(index, { overageBehavior: behavior })}
                disabled={busy}
              >
                {t(`creditLimits.behavior_${behavior}`)}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => onChange(drafts.filter((_, at) => at !== index))}
            disabled={busy}
          >
            {t('creditLimits.delete')}
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={() => next && onChange([...drafts, next])}
        disabled={busy || !next}
        title={next ? undefined : t('quotaProfiles.duplicateDimension')}
      >
        {t('quotaProfiles.addLimit')}
      </button>
      <p className="muted">{t('quotaProfiles.exposureWarning')}</p>
      <p className="muted">{t('creditLimits.noCounterReset')}</p>
      <p className="muted">{t('creditLimits.modelScopeNote')}</p>
    </div>
  );
}
